import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ApiURL from "../Api/Api";
import {
  ArrowPathIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ChevronDownIcon,
  XMarkIcon,
  Squares2X2Icon,
  TableCellsIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckBadgeIcon,
  CalendarIcon,
  MapPinIcon,
  TagIcon,
  ChatBubbleLeftIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import TransactionProofImage from "./TransactionProofImage";

/* ── helpers ─────────────────────────────────────────────────── */
const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(Number(v))
    : "₹0";

const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d)
    ? v
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/* ── status badge ─────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const s = (status || "").toUpperCase();
  const map = {
    SAVE: {
      cls: "bg-amber-50 text-amber-700 border-amber-200",
      icon: ClockIcon,
      label: "Saved",
    },
    SUBMIT: {
      cls: "bg-blue-50 text-blue-700 border-blue-200",
      icon: PaperAirplaneIcon,
      label: "Submitted",
    },
    RECEIVED: {
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      icon: CheckBadgeIcon,
      label: "Received",
    },
  };
  const { cls, icon: Icon, label } = map[s] || map.SAVE;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border ${cls}`}
    >
      <Icon className="w-2.5 h-2.5" />
      {label}
    </span>
  );
};

/* ── type badge ───────────────────────────────────────────────── */
const TypeBadge = ({ type }) =>
  type === "credit" ? (
    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
      <ArrowTrendingUpIcon className="w-2.5 h-2.5" /> Credit
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
      <ArrowTrendingDownIcon className="w-2.5 h-2.5" /> Debit
    </span>
  );

/* ── compact editable field ───────────────────────────────────── */
const EditField = ({ label, value, field, balanceId, onChange, disabled, type = "text", options, icon: Icon }) => {
  const inputCls =
    "w-full h-8 px-2 text-[11px] font-medium text-slate-700 bg-white border border-indigo-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition-all";
  const disabledCls =
    "w-full h-8 px-2 text-[11px] font-medium text-slate-400 bg-slate-50 border border-slate-200 rounded-lg flex items-center";

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="flex items-center gap-1 text-[9px] font-bold text-slate-400 uppercase tracking-widest">
          {Icon && <Icon className="w-2.5 h-2.5" />}
          {label}
        </label>
      )}
      {disabled ? (
        <div className={disabledCls}>{value || "—"}</div>
      ) : options ? (
        <div className="relative">
          <select
            value={value}
            onChange={(e) => onChange(balanceId, field, e.target.value)}
            className={inputCls + " appearance-none pr-6 cursor-pointer"}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
        </div>
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(balanceId, field, e.target.value)}
          className={inputCls}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
const CreateTransactionReport = ({ onRefresh }) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [formData, setFormData] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return "cards";
    return "table";
  });
  const [filterStatus, setFilterStatus] = useState("all");
  const [confirmModal, setConfirmModal] = useState(null);

  const user_id = localStorage.getItem("id");
  const role = localStorage.getItem("role");
  const user_name = localStorage.getItem("user_name");

  const recipientOptions = recipients.map((r) => ({
    value: r.user_name,
    label: `${r.user_name}${r.role ? ` (${r.role})` : ""}`,
  }));

  const getParticularOptions = (currentValue) => {
    if (!currentValue || recipientOptions.some((o) => o.value === currentValue)) {
      return recipientOptions;
    }
    return [{ value: currentValue, label: currentValue }, ...recipientOptions];
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && viewMode === "table") {
        setViewMode("cards");
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  /* ── fetch ─────────────────────────────────────────────────── */
  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `${ApiURL}/getCreationReport.php?user_id=${user_id}&role=${role}&user_name=${user_name}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTransactions(data);
      const fd = {};
      data.forEach((txn) => {
        fd[txn.balance_id] = {
          transaction_date: txn.transaction_date || "",
          amount_type: txn.amount_type || "",
          credit_amount: txn.credit_amount ? parseFloat(txn.credit_amount).toFixed(2) : "",
          debit_amount: txn.debit_amount ? parseFloat(txn.debit_amount).toFixed(2) : "",
          particuler_to: txn.particuler_to || "",
          site: txn.site || "",
          main_head: txn.main_head || "",
          sub_head: txn.sub_head || "",
          remarks: txn.remarks || "",
          form_status: txn.form_status || "",
          net_balance: txn.net_balance ? parseFloat(txn.net_balance).toFixed(2) : "",
          created_by: txn.created_by || "",
          created_at: txn.created_at || "",
          user_transaction_no: txn.transaction_no || "",
        };
      });
      setFormData(fd);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user_id || !role || !user_name) {
      setError("User not logged in");
      setIsLoading(false);
      return;
    }
    fetchTransactions();
    fetchFormOptions();
  }, []);

  useEffect(() => {
    if (!onRefresh) return;
    const t = setTimeout(fetchTransactions, 500);
    return () => clearTimeout(t);
  }, [onRefresh]);

  const handleInputChange = (balanceId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [balanceId]: { ...prev[balanceId], [field]: value },
    }));
  };

  const toggleRow = (id) =>
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () => {
    const selectable = filteredTxns
      .filter((t) => t.form_status?.toUpperCase() !== "RECEIVED")
      .map((t) => t.balance_id);
    setSelectedRows((prev) =>
      prev.length === selectable.length ? [] : selectable
    );
  };

  /* ── derived ───────────────────────────────────────────────── */
  const filteredTxns = transactions.filter((t) => {
    if (filterStatus === "all") return true;
    return (t.form_status || "").toUpperCase() === filterStatus.toUpperCase();
  });

  const selectableCount = filteredTxns.filter(
    (t) => t.form_status?.toUpperCase() !== "RECEIVED"
  ).length;
  const allSelected =
    selectableCount > 0 && selectedRows.length === selectableCount;

  const stats = {
    total: transactions.length,
    saved: transactions.filter((t) => t.form_status?.toUpperCase() === "SAVE").length,
    submitted: transactions.filter((t) => t.form_status?.toUpperCase() === "SUBMIT").length,
    received: transactions.filter((t) => t.form_status?.toUpperCase() === "RECEIVED").length,
  };

  /* ── submit ────────────────────────────────────────────────── */
  const handleStatusSubmit = async () => {
    setConfirmModal(null);
    if (!selectedRows.length) return;
    const valid = selectedRows.filter(
      (id) => formData[id]?.form_status?.toUpperCase() === "SAVE"
    );
    if (!valid.length) {
      alert('Only "Saved" transactions can be submitted.');
      return;
    }
    const payload = {
      user_id: parseInt(user_id, 10),
      role,
      rows: valid
        .map((id) => {
          const txn = transactions.find((t) => t.balance_id === id);
          return { user_transaction_no: parseInt(txn?.transaction_no || 0, 10) };
        })
        .filter((r) => r.user_transaction_no !== 0),
    };
    setIsSubmitting(true);
    try {
      const res = await fetch(`${ApiURL}/txnsubmit.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Submit failed");
      if (Array.isArray(result.warnings) && result.warnings.length) {
        alert(`Submitted with warning:\n${result.warnings.join("\n")}`);
      }
      await fetchTransactions();
      setSelectedRows([]);
    } catch (e) {
      alert(`Submit failed: ${e.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── update ────────────────────────────────────────────────── */
  const handleUpdate = async () => {
    setConfirmModal(null);
    if (!selectedRows.length) return;
    const valid = selectedRows.filter(
      (id) => formData[id]?.form_status?.toUpperCase() === "SAVE"
    );
    if (!valid.length) {
      alert('Only "Saved" transactions can be updated.');
      return;
    }
    const rows = valid
      .map((id) => {
        const txn = transactions.find((t) => t.balance_id === id);
        const cr = parseFloat(String(formData[id].credit_amount || "0").replace(/,/g, "")) || 0;
        const dr = parseFloat(String(formData[id].debit_amount || "0").replace(/,/g, "")) || 0;
        const nb = parseFloat(String(formData[id].net_balance || "0").replace(/,/g, "")) || 0;
        return {
          user_transaction_no: parseInt(txn?.transaction_no || 0, 10),
          transaction_date: formData[id].transaction_date || "",
          amount_type: txn?.amount_type || formData[id].amount_type || "",
          credit_amount: Number(cr.toFixed(2)),
          debit_amount: Number(dr.toFixed(2)),
          net_balance: Number(nb.toFixed(2)),
          particuler_to: formData[id].particuler_to || "",
          site: formData[id].site || "",
          main_head: formData[id].main_head || "",
          sub_head: formData[id].sub_head || "",
          remarks: formData[id].remarks || "",
        };
      })
      .filter((r) => r.user_transaction_no !== 0);
    if (!rows.length) return;
    setIsUpdating(true);
    try {
      const res = await fetch(`${ApiURL}/Updatetxnform.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: parseInt(user_id, 10), role, rows }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Update failed");
      await fetchTransactions();
      setSelectedRows([]);
    } catch (e) {
      alert(`Update failed: ${e.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    setConfirmModal(null);
    if (!selectedRows.length) return;
    const valid = selectedRows.filter(
      (id) => formData[id]?.form_status?.toUpperCase() === "SAVE"
    );
    if (!valid.length) {
      alert('Only "Saved" transactions can be deleted.');
      return;
    }
    const rows = valid
      .map((id) => {
        const txn = transactions.find((t) => t.balance_id === id);
        return { user_transaction_no: parseInt(txn?.transaction_no || 0, 10) };
      })
      .filter((r) => r.user_transaction_no !== 0);
    if (!rows.length) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`${ApiURL}/DeleteTxnForm.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: parseInt(user_id, 10), role, rows }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Delete failed");
      await fetchTransactions();
      setSelectedRows([]);
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const res = await fetch(`${ApiURL}/getTransactionForm.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setRecipients(data.recipients || data.workers || []);
      }
    } catch (e) {
      console.error("Failed to load transaction form options", e);
    }
  };

  /* ── CSV export ─────────────────────────────────────────────── */
  const exportCSV = () => {
    if (!transactions.length) return;
    const headers = [
      "#", "Txn No", "Date", "Type", "Credit", "Debit", "Balance",
      "Particulars", "Site", "Main Head", "Sub Head", "Remarks",
      "Status", "Created By", "Created At",
    ];
    const rows = transactions.map((t, i) => [
      i + 1, t.display_transaction_no || t.transaction_no,
      t.transaction_date, t.amount_type,
      t.credit_amount || 0, t.debit_amount || 0, t.net_balance || 0,
      t.particuler_to || "", t.site || "", t.main_head || "",
      t.sub_head || "", t.remarks || "", t.form_status || "",
      t.created_by || "", t.created_at || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r.map((c) => {
          const v = String(c ?? "");
          return v.includes(",") ? `"${v}"` : v;
        }).join(",")
      )
      .join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "transactions.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* ── Transaction Card (used on mobile + card view) ──────────── */
  const TransactionCard = ({ txn, index }) => {
    const isReceived = txn.form_status?.toUpperCase() === "RECEIVED";
    const selected = selectedRows.includes(txn.balance_id);
    const fd = formData[txn.balance_id] || {};

    return (
      <motion.div
        key={txn.balance_id}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(index * 0.03, 0.25) }}
        className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${
          selected
            ? "border-indigo-300 ring-2 ring-indigo-100"
            : "border-slate-100"
        }`}
      >
        {/* Color bar */}
        <div
          className={`h-0.5 w-full ${
            txn.amount_type === "credit" ? "bg-emerald-400" : "bg-red-400"
          }`}
        />
        <div className="px-4 py-3">
          {/* Top row */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2.5 min-w-0">
              {!isReceived && (
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleRow(txn.balance_id)}
                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0 cursor-pointer mt-0.5"
                />
              )}
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-slate-800 truncate">
                  {fd.particuler_to || "—"}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {txn.display_transaction_no || txn.transaction_no} ·{" "}
                  {fd.site || "—"}
                </p>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p
                className={`text-[15px] font-bold ${
                  txn.amount_type === "credit"
                    ? "text-emerald-600"
                    : "text-red-600"
                }`}
              >
                {txn.amount_type === "credit" ? "+" : "−"}
                {fmt(
                  txn.amount_type === "credit"
                    ? fd.credit_amount
                    : fd.debit_amount
                )}
              </p>
              <p className="text-[10px] text-slate-400">
                Bal {fmt(fd.net_balance)}
              </p>
            </div>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            <TypeBadge type={txn.amount_type} />
            <StatusBadge status={fd.form_status} />
            <TransactionProofImage path={txn.attachment_path} />
            {fd.main_head && (
              <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100">
                {fd.main_head}
              </span>
            )}
          </div>

          {/* Editable fields — only SAVE */}
          {!isReceived && (
            <div className="grid grid-cols-2 gap-2 pt-2.5 border-t border-slate-100">
              <EditField
                label="Date"
                icon={CalendarIcon}
                value={fd.transaction_date}
                field="transaction_date"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
                type="date"
              />
              <EditField
                label="Type"
                icon={TagIcon}
                value={fd.amount_type}
                field="amount_type"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
                options={[
                  { value: "credit", label: "Credit" },
                  { value: "debit", label: "Debit" },
                ]}
                disabled
              />
              <EditField
                label="Credit"
                value={fd.credit_amount}
                field="credit_amount"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
                type="number"
                disabled={role === "worker" || txn.amount_type !== "credit"}
              />
              <EditField
                label="Debit"
                value={fd.debit_amount}
                field="debit_amount"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
                type="number"
                disabled={txn.amount_type !== "debit"}
              />
              <EditField
                label="Particulars"
                value={fd.particuler_to}
                field="particuler_to"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
                options={getParticularOptions(fd.particuler_to)}
              />
              <EditField
                label="Site"
                icon={MapPinIcon}
                value={fd.site}
                field="site"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
              />
              <EditField
                label="Remarks"
                icon={ChatBubbleLeftIcon}
                value={fd.remarks}
                field="remarks"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
              />
              <EditField
                label="Main Head"
                value={fd.main_head}
                field="main_head"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
              />
              <EditField
                label="Sub Head"
                value={fd.sub_head}
                field="sub_head"
                balanceId={txn.balance_id}
                onChange={handleInputChange}
              />
            </div>
          )}

          <div className="mt-2.5 flex items-center justify-between pt-2 border-t border-slate-50">
            <p className="text-[10px] text-slate-400">
              {fmtDateTime(fd.created_at)} · {fd.created_by}
            </p>
            {!isReceived && (
              <button
                onClick={() => toggleRow(txn.balance_id)}
                className={`text-[10px] font-bold px-2.5 py-1 rounded-lg transition-all ${
                  selected
                    ? "bg-indigo-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600"
                }`}
              >
                {selected ? "✓ Selected" : "Select"}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  /* ── Responsive Table View (scrollable horizontally on mobile) ── */
  const TableView = () => (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {/* Responsive wrapper - allows horizontal scroll on mobile */}
      <div className="overflow-x-auto">
        <div className="min-w-[800px] lg:min-w-0">
          <table className="w-full text-[11px] text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="px-2 py-2.5 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                </th>
                {[
                  "Txn",
                  "Date",
                  "Type",
                  "Credit",
                  "Debit",
                  "Balance",
                  "Particulars",
                  "Site",
                  "Head",
                  "Proof",
                  "Status",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-2 py-2.5 text-[9px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredTxns.map((txn, i) => {
                const isReceived = txn.form_status?.toUpperCase() === "RECEIVED";
                const selected = selectedRows.includes(txn.balance_id);
                const fd = formData[txn.balance_id] || {};
                return (
                  <motion.tr
                    key={txn.balance_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(i * 0.015, 0.2) }}
                    className={`transition-colors ${
                      selected
                        ? "bg-indigo-50/60"
                        : isReceived
                        ? "bg-emerald-50/30"
                        : "hover:bg-slate-50/60"
                    }`}
                  >
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={isReceived}
                        onChange={() => toggleRow(txn.balance_id)}
                        className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 disabled:opacity-30 cursor-pointer"
                      />
                    </td>
                    <td className="px-2 py-1.5 font-bold text-slate-600 whitespace-nowrap text-[10px]">
                      {txn.display_transaction_no || txn.transaction_no}
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap">
                      <input
                        type="date"
                        value={fd.transaction_date}
                        disabled={isReceived}
                        onChange={(e) =>
                          handleInputChange(txn.balance_id, "transaction_date", e.target.value)
                        }
                        className={`w-full h-7 px-1.5 text-[10px] font-medium text-slate-700 border rounded-lg outline-none transition-all ${
                          isReceived
                            ? "bg-slate-50 border-slate-100 text-slate-400"
                            : "bg-white border-indigo-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100"
                        }`}
                      />
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap">
                      {isReceived ? (
                        <TypeBadge type={fd.amount_type} />
                      ) : (
                        <div className="relative">
                          <select
                            value={fd.amount_type}
                            disabled
                            onChange={(e) =>
                              handleInputChange(txn.balance_id, "amount_type", e.target.value)
                            }
                            className="w-full h-7 px-1.5 pr-5 text-[10px] text-slate-400 bg-slate-50 border border-slate-100 rounded-lg outline-none appearance-none"
                          >
                            <option value="credit">Credit</option>
                            <option value="debit">Debit</option>
                          </select>
                          <ChevronDownIcon className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-slate-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap">
                      <input
                        type="number"
                        value={fd.credit_amount}
                        disabled={isReceived || role === "worker" || txn.amount_type !== "credit"}
                        onChange={(e) =>
                          handleInputChange(txn.balance_id, "credit_amount", e.target.value)
                        }
                        className={`w-full h-7 px-1.5 text-[10px] font-medium text-emerald-700 border rounded-lg outline-none transition-all ${
                          isReceived || role === "worker" || txn.amount_type !== "credit"
                            ? "bg-slate-50 border-slate-100 text-slate-400"
                            : "bg-white border-indigo-200 focus:border-indigo-400"
                        }`}
                      />
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap">
                      <input
                        type="number"
                        value={fd.debit_amount}
                        disabled={isReceived || txn.amount_type !== "debit"}
                        onChange={(e) =>
                          handleInputChange(txn.balance_id, "debit_amount", e.target.value)
                        }
                        className={`w-full h-7 px-1.5 text-[10px] font-medium text-red-700 border rounded-lg outline-none transition-all ${
                          isReceived || txn.amount_type !== "debit"
                            ? "bg-slate-50 border-slate-100 text-slate-400"
                            : "bg-white border-indigo-200 focus:border-indigo-400"
                        }`}
                      />
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className="font-bold text-slate-700 text-[10px]">
                        {fmt(fd.net_balance)}
                      </span>
                    </td>
                    <td className="px-1 py-1.5">
                      {isReceived ? (
                        <input
                          type="text"
                          value={fd.particuler_to}
                          disabled
                          className="w-full h-7 px-1.5 text-[10px] text-slate-400 bg-slate-50 border border-slate-100 rounded-lg outline-none truncate"
                        />
                      ) : (
                        <div className="relative">
                          <select
                            value={fd.particuler_to}
                            onChange={(e) =>
                              handleInputChange(txn.balance_id, "particuler_to", e.target.value)
                            }
                            className="w-full h-7 px-1.5 pr-5 text-[10px] font-medium text-slate-700 bg-white border border-indigo-200 rounded-lg outline-none appearance-none focus:border-indigo-400"
                          >
                            <option value="">Select</option>
                            {getParticularOptions(fd.particuler_to).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDownIcon className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-slate-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap">
                      <input
                        type="text"
                        value={fd.site}
                        disabled={isReceived}
                        onChange={(e) =>
                          handleInputChange(txn.balance_id, "site", e.target.value)
                        }
                        className={`w-full h-7 px-1.5 text-[10px] font-medium text-slate-700 border rounded-lg outline-none transition-all ${
                          isReceived
                            ? "bg-slate-50 border-slate-100 text-slate-400"
                            : "bg-white border-indigo-200 focus:border-indigo-400"
                        }`}
                      />
                    </td>
                    <td className="px-1 py-1.5 whitespace-nowrap">
                      <input
                        type="text"
                        value={fd.main_head}
                        disabled={isReceived}
                        onChange={(e) =>
                          handleInputChange(txn.balance_id, "main_head", e.target.value)
                        }
                        className={`w-full h-7 px-1.5 text-[10px] font-medium text-slate-700 border rounded-lg outline-none transition-all ${
                          isReceived
                            ? "bg-slate-50 border-slate-100 text-slate-400"
                            : "bg-white border-indigo-200 focus:border-indigo-400"
                        }`}
                      />
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <TransactionProofImage path={txn.attachment_path} />
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <StatusBadge status={fd.form_status} />
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Table footer */}
      <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
        <p className="text-[11px] text-slate-400">
          {filteredTxns.length} records ·{" "}
          <span className="font-bold text-slate-600">{selectedRows.length}</span>{" "}
          selected
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => selectedRows.length && setConfirmModal("delete")}
            disabled={!selectedRows.length}
            className="flex items-center gap-1 text-[11px] font-bold text-red-600 hover:text-red-700 disabled:opacity-40"
          >
            <TrashIcon className="w-3.5 h-3.5" /> Delete
          </button>
          <span className="text-slate-200">Â·</span>
          <button
            onClick={() => selectedRows.length && setConfirmModal("update")}
            disabled={!selectedRows.length}
            className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-700 disabled:opacity-40"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" /> Update
          </button>
          <span className="text-slate-200">·</span>
          <button
            onClick={() => selectedRows.length && setConfirmModal("submit")}
            disabled={!selectedRows.length}
            className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 hover:text-emerald-700 disabled:opacity-40"
          >
            <PaperAirplaneIcon className="w-3.5 h-3.5" /> Submit
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-6">
      {/* ── Section header ───────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <div>
          <h2 className="text-base font-bold text-slate-800">
            Recent Transactions
          </h2>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {filteredTxns.length} of {transactions.length} records
          </p>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* View toggle */}
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-0.5 shadow-sm">
            <button
              onClick={() => setViewMode("table")}
              className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${
                viewMode === "table"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-slate-600"
              }`}
              aria-label="Table view"
            >
              <TableCellsIcon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode("cards")}
              className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${
                viewMode === "cards"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-slate-600"
              }`}
              aria-label="Card view"
            >
              <Squares2X2Icon className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            onClick={exportCSV}
            disabled={!transactions.length}
            className="flex items-center gap-1.5 h-8 px-3 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 shadow-sm disabled:opacity-40 transition-all"
          >
            <DocumentArrowDownIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button
            onClick={fetchTransactions}
            className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-indigo-600 shadow-sm transition-all"
          >
            <ArrowPathIcon
              className={`w-3.5 h-3.5 ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* ── Mini stats ─────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          {
            label: "Total",
            val: stats.total,
            bg: "bg-indigo-50",
            text: "text-indigo-700",
            iconBg: "bg-indigo-500",
            icon: BanknotesIcon,
          },
          {
            label: "Saved",
            val: stats.saved,
            bg: "bg-amber-50",
            text: "text-amber-700",
            iconBg: "bg-amber-500",
            icon: ClockIcon,
          },
          {
            label: "Submitted",
            val: stats.submitted,
            bg: "bg-blue-50",
            text: "text-blue-700",
            iconBg: "bg-blue-500",
            icon: PaperAirplaneIcon,
          },
          {
            label: "Received",
            val: stats.received,
            bg: "bg-emerald-50",
            text: "text-emerald-700",
            iconBg: "bg-emerald-500",
            icon: CheckBadgeIcon,
          },
        ].map(({ label, val, bg, text, iconBg, icon: Icon }) => (
          <div
            key={label}
            className={`${bg} rounded-2xl p-2.5 sm:p-3 flex items-center gap-2`}
          >
            <div
              className={`${iconBg} w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0`}
            >
              <Icon className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-bold ${text} leading-tight`}>{val}</p>
              <p className="text-[9px] text-slate-400 font-semibold uppercase tracking-wider truncate">
                {label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2.5 mb-4 shadow-sm flex flex-wrap items-center gap-2">
        {/* Status filter */}
        <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl p-1">
          {["all", "save", "submit", "received"].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold capitalize transition-all ${
                filterStatus === s
                  ? s === "save"
                    ? "bg-amber-500 text-white shadow-sm"
                    : s === "submit"
                    ? "bg-blue-500 text-white shadow-sm"
                    : s === "received"
                    ? "bg-emerald-500 text-white shadow-sm"
                    : "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {s === "all"
                ? "All"
                : s === "save"
                ? "Saved"
                : s === "submit"
                ? "Submitted"
                : "Received"}
            </button>
          ))}
        </div>

        {/* Selection pill */}
        {selectedRows.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-indigo-600 font-bold bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-full">
              {selectedRows.length} selected
            </span>
            <button
              onClick={() => setSelectedRows([])}
              className="text-slate-400 hover:text-slate-600"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <div className="flex gap-2 ml-auto">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => selectedRows.length && setConfirmModal("delete")}
            disabled={isDeleting || isLoading || !selectedRows.length}
            className="flex items-center gap-1.5 h-8 px-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <TrashIcon className="w-3.5 h-3.5" />
            )}
            Delete
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => selectedRows.length && setConfirmModal("update")}
            disabled={isUpdating || isLoading || !selectedRows.length}
            className="flex items-center gap-1.5 h-8 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <PencilSquareIcon className="w-3.5 h-3.5" />
            )}
            Update
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => selectedRows.length && setConfirmModal("submit")}
            disabled={isSubmitting || isLoading || !selectedRows.length}
            className="flex items-center gap-1.5 h-8 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <PaperAirplaneIcon className="w-3.5 h-3.5" />
            )}
            Submit
          </motion.button>
        </div>
      </div>

      {/* ── States ─────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl h-14 animate-pulse border border-slate-100"
            />
          ))}
        </div>
      ) : error ? (
        <div className="bg-white border border-red-100 rounded-2xl py-12 flex flex-col items-center gap-2 shadow-sm">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-300" />
          <p className="text-sm text-red-500 font-medium">{error}</p>
          <button
            onClick={fetchTransactions}
            className="text-xs text-indigo-500 font-bold mt-1"
          >
            Retry
          </button>
        </div>
      ) : filteredTxns.length === 0 ? (
        <div className="bg-white border border-slate-100 rounded-2xl py-12 flex flex-col items-center gap-2 shadow-sm">
          <BanknotesIcon className="w-10 h-10 text-slate-200" />
          <p className="text-sm text-slate-400">No transactions found</p>
          {filterStatus !== "all" && (
            <button
              onClick={() => setFilterStatus("all")}
              className="text-xs text-indigo-500 font-bold"
            >
              Clear filter
            </button>
          )}
        </div>
      ) : (
        <>
          {/* CARD VIEW — works on all screen sizes */}
          {viewMode === "cards" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredTxns.map((txn, i) => (
                <TransactionCard key={txn.balance_id} txn={txn} index={i} />
              ))}
            </div>
          )}

          {/* TABLE VIEW — responsive with horizontal scroll on mobile */}
          {viewMode === "table" && <TableView />}
        </>
      )}

      {/* ── Confirm modal ──────────────────────────────────────── */}
      <AnimatePresence>
        {confirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={(e) =>
              e.target === e.currentTarget && setConfirmModal(null)
            }
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 w-full max-w-sm"
            >
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${
                  confirmModal === "submit"
                    ? "bg-emerald-50"
                    : confirmModal === "delete"
                    ? "bg-red-50"
                    : "bg-indigo-50"
                }`}
              >
                {confirmModal === "submit" ? (
                  <PaperAirplaneIcon className="w-6 h-6 text-emerald-600" />
                ) : confirmModal === "delete" ? (
                  <TrashIcon className="w-6 h-6 text-red-600" />
                ) : (
                  <PencilSquareIcon className="w-6 h-6 text-indigo-600" />
                )}
              </div>
              <p className="text-base font-bold text-slate-800 mb-1">
                {confirmModal === "submit"
                  ? "Submit Transactions?"
                  : confirmModal === "delete"
                  ? "Delete Transactions?"
                  : "Update Transactions?"}
              </p>
              <p className="text-[12px] text-slate-500 mb-5">
                {confirmModal === "submit"
                  ? `${selectedRows.length} transaction${
                      selectedRows.length !== 1 ? "s" : ""
                    } will be submitted for approval. Only Saved transactions are processed.`
                  : confirmModal === "delete"
                  ? `${selectedRows.length} transaction${
                      selectedRows.length !== 1 ? "s" : ""
                    } will be permanently deleted. Only Saved transactions are processed.`
                  : `${selectedRows.length} transaction${
                      selectedRows.length !== 1 ? "s" : ""
                    } will be updated with the current values. Only Saved transactions are processed.`}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmModal(null)}
                  className="flex-1 h-10 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={
                    confirmModal === "submit"
                      ? handleStatusSubmit
                      : confirmModal === "delete"
                      ? handleDelete
                      : handleUpdate
                  }
                  className={`flex-1 h-10 rounded-xl text-white text-xs font-bold transition-colors shadow-sm ${
                    confirmModal === "submit"
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : confirmModal === "delete"
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreateTransactionReport;
